import React, { FunctionComponent, useCallback, useMemo } from 'react'
import styles from './StatisticsInfo.module.sass'
import StatusInfo from '../StatusInfo/StatusInfo'
import DifferenceButton from '../DifferenceButton/DifferenceButton'
import { Classes, Colors } from '../../../../styles'
import { useRootDispatch, useRootSelector } from '../../../../utils/hooks'
import { ByClientData, StatisticsRow, StatisticsType } from '../../../../responses/statistics'
import clsx from 'clsx'
import Chart, { ChartData } from '../Chart/Chart'
import { pipe } from 'fp-ts/function'
import * as A from 'fp-ts/Array'
import * as O from 'fp-ts/Option'
import { contramap, ordNumber } from 'fp-ts/Ord'
import Splitter from '../../../etc/Splitter/Splitter'
import { pick } from '../../../../utils/utils'
import { byClientActions } from '../../../../redux/pages/statistics/byClient'
import {RequestStatus} from '../../../../responses/response'

const selectors: Record<StatisticsType, (row: StatisticsRow<ByClientData>) => [number, number]> = {
    'task-opened': row => row.total.tasks.opened,
    'task-closed': row => row.total.tasks.closed,
    'project-opened': row => row.total.projects.opened,
    'project-closed': row => row.total.projects.closed,
    'project-canceled': row => row.total.projects.canceled,
    'object-design-won': row => row.total.project_work_objects.design.won,
    'object-design-lost': row => row.total.project_work_objects.design.lost,
    'object-design-canceled': row => row.total.project_work_objects.design.canceled,
    'object-production-won': row => row.total.project_work_objects.production.won,
    'object-production-lost': row => row.total.project_work_objects.production.lost,
    'object-production-canceled': row => row.total.project_work_objects.production.canceled,
}

const ordChartData = contramap((item: ChartData) => item.current)(ordNumber)

const prepareData = (type: StatisticsType, rows: StatisticsRow<ByClientData>[]): ChartData[] => {
    const [init, last] = pipe(
        rows,
        A.map(item => {
            const [current, previous] = selectors[type](item)
            return {
                name: item.name,
                current,
                previous,
            }
        }),
        A.sort(ordChartData),
        A.reverse,
        A.splitAt(11)
    )

    const others = A.isNonEmpty(last) ?
        O.some(last.reduce((acc, item: ChartData) => ({
                name: acc.name,
                current: acc.current + item.current,
                previous: acc.previous + item.previous,
            }), {
                name: 'Остальное',
                current: 0,
                previous: 0,
            }
        )): O.none

    return init.concat(
        O.fold<ChartData, ChartData[]>(() => [], item => [item])(others)
    )
}

const empty = () => [0, 0] as [number, number]

const StatisticsInfo: FunctionComponent = () => {
    const dispatch = useRootDispatch()
    const {type, data, periods, state} = useRootSelector(state => pick(state.pages.statistics.byClient, 'type', 'data', 'periods', 'state'))

    const chartData = useMemo(() => pipe(
        data,
        O.chain(data => O.fromNullable(data.rows)),
        O.fold(
            () => [],
            data => prepareData(type, data)
        )
    ), [data, type])

    const typeChange = useCallback((type: StatisticsType) => dispatch(byClientActions.setType(type)), [dispatch])

    return <div className={styles.info}>
        <div className={styles.projects}>
            <div className={clsx(styles.title, styles.span)}>Проекты:</div>
            <StatusInfo color={Colors.orange} status={'Открыто'}/>

            <DifferenceButton
                type={'project-opened'}
                active={type === 'project-opened'}
                value={pipe(data, O.fold(empty, data => data.total.projects.opened))}
                onClick={typeChange}
                periods={periods}
                hide={state === RequestStatus.FETCHING}
            />

            <StatusInfo color={Colors.green} status={'Закрыто'}/>
            <DifferenceButton
                type={'project-closed'}
                active={type === 'project-closed'}
                value={pipe(data, O.fold(empty, data => data.total.projects.closed))}
                onClick={typeChange}
                periods={periods}
                hide={state === RequestStatus.FETCHING}
            />
            <StatusInfo color={Colors.olive} status={'Отменено'}/>
            <DifferenceButton
                type={'project-canceled'}
                active={type === 'project-canceled'}
                value={pipe(data, O.fold(empty, data => data.total.projects.canceled))}
                onClick={typeChange}
                periods={periods}
                hide={state === RequestStatus.FETCHING}
            />
        </div>
        <Splitter full/>
        <div className={styles.objects}>
            <div className={styles.title}>Объекты:</div>
            <div className={Classes.f11}>Дизайн:</div>
            <div className={styles.f11}>Производство:</div>
            <StatusInfo color={Colors.orange} status={'Выиграно'}/>
            <DifferenceButton
                type={'object-design-won'}
                active={type === 'object-design-won'}
                value={pipe(data, O.fold(empty, data => data.total.project_work_objects.design.won))}
                onClick={typeChange}
                periods={periods}
                hide={state === RequestStatus.FETCHING}
            />
            <DifferenceButton
                type={'object-production-won'}
                active={type === 'object-production-won'}
                value={pipe(data, O.fold(empty, data => data.total.project_work_objects.production.won))}
                onClick={typeChange}
                periods={periods}
                hide={state === RequestStatus.FETCHING}
            />
            <StatusInfo color={Colors.green} status={'Проиграно'}/>
            <DifferenceButton
                type={'object-design-lost'}
                active={type === 'object-design-lost'}
                value={pipe(data, O.fold(empty, data => data.total.project_work_objects.design.lost))}
                onClick={typeChange}
                periods={periods}
                hide={state === RequestStatus.FETCHING}
            />
            <DifferenceButton
                type={'object-production-lost'}
                active={type === 'object-production-lost'}
                value={pipe(data, O.fold(empty, data => data.total.project_work_objects.production.lost))}
                onClick={typeChange}
                periods={periods}
                hide={state === RequestStatus.FETCHING}
            />
            <StatusInfo color={Colors.olive} status={'Отменено'}/>
            <DifferenceButton
                type={'object-design-canceled'}
                active={type === 'object-design-canceled'}
                value={pipe(data, O.fold(empty, data => data.total.project_work_objects.design.canceled))}
                onClick={typeChange}
                periods={periods}
                hide={state === RequestStatus.FETCHING}
            />
            <DifferenceButton
                type={'object-production-canceled'}
                active={type === 'object-production-canceled'}
                value={pipe(data, O.fold(empty, data => data.total.project_work_objects.production.canceled))}
                onClick={typeChange}
                periods={periods}
                hide={state === RequestStatus.FETCHING}
            />
        </div>
        <Splitter full/>
        <div className={styles.tasks}>
            <div className={clsx(styles.title, styles.span)}>Задачи:</div>
            <StatusInfo color={Colors.orange} status={'Открыто'}/>
            <DifferenceButton
                type={'task-opened'}
                active={type === 'task-opened'}
                value={pipe(data, O.fold(empty, data => data.total.tasks.opened))}
                onClick={typeChange}
                periods={periods}
                hide={state === RequestStatus.FETCHING}
            />
            <StatusInfo color={Colors.green} status={'Закрыто'}/>
            <DifferenceButton
                type={'task-closed'}
                active={type === 'task-closed'}
                value={pipe(data, O.fold(empty, data => data.total.tasks.closed))}
                onClick={typeChange}
                periods={periods}
                hide={state === RequestStatus.FETCHING}
            />
        </div>
        <Splitter full/>
        <Chart data={chartData} periods={periods} hide={state === RequestStatus.FETCHING}/>
    </div>
}
export default StatisticsInfo